/**
 * ConfigCrossTablesPanel — per-template (per-config) cross-table values (editor "Value
 * translations" tab, below the cross-table MAPPING panel).
 *
 * The mapping panel above says WHICH org X-table + column a field uses. THIS panel lets a lab
 * give a config its OWN values for an X-table, uploaded as an X*.mdb workbook or a single
 * X*.csv. At generation those values OVERRIDE the org store per X-table for this config — an
 * X-table the config defines replaces the org's; X-tables it doesn't define fall back to the org.
 *
 * Security: config_id comes from the editor route (org-scoped server-side); upload + delete are
 * admin-only (the tab is already admin-gated, and the backend re-checks). readOnly hides every
 * mutating affordance — list only.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Dropzone } from "../../components/Dropzone";
import { Modal } from "../../components/Modal";
import { TextField } from "../../components/TextField";
import { LoadingBlock } from "../../components/Spinner";
import { EmptyState } from "../../components/EmptyState";
import { useToast } from "../../components/Toast";
import { errorMessage } from "../../api/client";
import {
  useConfigCrossTables,
  useDeleteConfigCrossTable,
  useUploadConfigCrossTable,
  type ConfigCrossTableSummary,
} from "../../api/configXtables";
import { useCreateBookFromConfig } from "../../api/codeBooks";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function ConfigCrossTablesPanel({
  configId,
  readOnly,
}: {
  configId: string;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const tablesQ = useConfigCrossTables(configId);
  const upload = useUploadConfigCrossTable(configId);
  const deleteMut = useDeleteConfigCrossTable(configId);
  const promote = useCreateBookFromConfig();

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [bookName, setBookName] = useState("");

  const tables: ConfigCrossTableSummary[] = tablesQ.data ?? [];

  const handleSaveAs = async () => {
    const name = bookName.trim();
    if (!name) return;
    try {
      const book = await promote.mutateAsync({ config_id: configId, name });
      toast.success(
        t("editor.crossTableValues.savedAsBook", { name: book.name, count: book.value_count }),
      );
      setSaveAsOpen(false);
      setBookName("");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const res = await upload.mutateAsync(file);
      toast.success(t("editor.crossTableValues.uploaded", { rows: res.rows_written }));
    } catch (err) {
      toast.error(t("editor.crossTableValues.uploadError", { detail: errorMessage(err) }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget);
      toast.success(t("editor.crossTableValues.deleteSuccess", { name: deleteTarget }));
    } catch (err) {
      toast.error(t("editor.crossTableValues.deleteError", { detail: errorMessage(err) }));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="card stack" role="region" aria-label={t("editor.crossTableValues.heading")}>
      <div className="row row--between row--wrap" style={{ gap: "var(--space-2)" }}>
        <div className="stack" style={{ gap: 4 }}>
          <h3 style={{ margin: 0 }}>{t("editor.crossTableValues.heading")}</h3>
          <p className="muted" style={{ margin: 0, maxWidth: 680 }}>
            {t("editor.crossTableValues.intro")}
          </p>
        </div>
        {!readOnly && tables.length > 0 && (
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setBookName("");
              setSaveAsOpen(true);
            }}
          >
            {t("editor.crossTableValues.saveAsBook")}
          </Button>
        )}
      </div>

      {!readOnly && (
        <Dropzone
          label={t("editor.crossTableValues.uploadLabel")}
          sublabel={t("editor.crossTableValues.uploadSublabel")}
          hint={t("editor.crossTableValues.uploadHint")}
          accept=".mdb,.accdb,.csv,application/x-msaccess,text/csv"
          loading={upload.isPending}
          loadingLabel={t("editor.crossTableValues.uploading")}
          onFile={(file) => void handleUpload(file)}
        />
      )}

      {tablesQ.isLoading ? (
        <LoadingBlock />
      ) : tables.length === 0 ? (
        <EmptyState title={t("editor.crossTableValues.empty")} />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t("editor.crossTableValues.colTable")}</th>
                <th>{t("editor.crossTableValues.colRows")}</th>
                <th>{t("editor.crossTableValues.colSource")}</th>
                <th>{t("editor.crossTableValues.colUpdated")}</th>
                {!readOnly && <th />}
              </tr>
            </thead>
            <tbody>
              {tables.map((tbl) => (
                <tr key={tbl.xtable}>
                  <td>
                    <Badge tone="default">{tbl.xtable}</Badge>
                  </td>
                  <td>{tbl.row_count}</td>
                  <td>{tbl.source_file}</td>
                  <td>{formatDate(tbl.updated_at)}</td>
                  {!readOnly && (
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setDeleteTarget(tbl.xtable)}
                        aria-label={t("common.delete")}
                      >
                        {t("common.delete")}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={t("editor.crossTableValues.confirmDeleteTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" loading={deleteMut.isPending} onClick={() => void handleDelete()}>
              {t("common.delete")}
            </Button>
          </>
        }
      >
        <p>{t("editor.crossTableValues.confirmDelete", { name: deleteTarget ?? "" })}</p>
      </Modal>

      <Modal
        open={saveAsOpen}
        onClose={() => setSaveAsOpen(false)}
        title={t("editor.crossTableValues.saveAsTitle")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSaveAsOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              loading={promote.isPending}
              disabled={!bookName.trim()}
              onClick={() => void handleSaveAs()}
            >
              {t("editor.crossTableValues.saveAsConfirm")}
            </Button>
          </>
        }
      >
        <p className="muted" style={{ marginTop: 0 }}>
          {t("editor.crossTableValues.saveAsIntro")}
        </p>
        <TextField
          label={t("editor.crossTableValues.saveAsNameLabel")}
          value={bookName}
          onChange={(e) => setBookName(e.target.value)}
        />
      </Modal>
    </div>
  );
}
